import { ProductBuyOutcome, Season, seasonSection, seasonView, setPassXpSink } from "@highmarch/app";
import { LevelPhase, LevelSim, SECONDS_PER_DAY, tierProgress } from "@highmarch/domain";
import { rowHtml } from "../ui/components";
import { uiUrl } from "../ui/portraits";
import { economyLink } from "../ui/hub/economyLink";
import { addDealsTab, dealsLink } from "../ui/deals/dealsLink";
import { openDeals } from "../ui/deals/dealsSheet";
import { openPass } from "../ui/season/passSheet";
import { PASS_ICON } from "../ui/season/passMarkup";
import { eventClock } from "./events";
import type { Feature, FeatureContext, HubApi } from "./registry";

/**
 * The season pass "War Campaign" in the hub (MP-22): the Pass button under Deals (the campaign banner over the navy
 * label plate, STYLE §2.16, with a red count of rewards to claim — a badge, never a pop-up), the Pass sheet
 * (ui/season/passSheet.ts) from it and from a tab at the end of the Deals strip, pass XP from every finished battle
 * (registry battleEnd, and the end screen's quiet "+100" line: a fact, never an offer), the `season` save section, the
 * premium products through PurchaseService's product handlers, a Guide topic and Bram's line, the away card's lines.
 * The rules are @highmarch/app season.ts (Daily Orders and event nodes are credited by its sync) and @highmarch/domain meta/season.ts.
 */

/** Top-bar order: after the Deals button (30). */
/** End-screen block order: after the VIP production line (10), before the events block (20). */
const PASS_END_ORDER = 15;

/** A run's key, as the events' end block keys it: level, seed, outcome and stages (counts once however it is reported). */
const runKey = (sim: LevelSim, won: boolean): string => `${sim.levelNumber}.${sim.seed.toString(36)}.${won ? "w" : "l"}${sim.stagesCleared}`;

export function createSeasonFeature(): Feature {
  return {
    id: "season",
    register(ctx, registry) {
      const state = Season.stateFromSave(ctx.balance, ctx.save);
      // the season skin goes through the catalog's skin grants: the cosmetics' `owned`, or held until they attach
      const season = new Season(ctx.catalog, state, ctx.catalog.skinGrants);
      seasonSection.bind(ctx.save, () => state.toJson());
      // The events' clock (`?evday` moves both in debug captures); the trusted-now floor is the app's (season.now).
      let shift = 0;
      const base = eventClock(() => ctx.now(), ctx.debug, typeof location === "undefined" ? "" : location.search);
      const now = (): number => base() + shift;
      let hubRef: HubApi | null = null;
      const shown = (): boolean => { const t = now(); season.sync(t); return season.open && (season.live(t) || state.summary !== null); };
      const dot = (): number => (shown() ? season.claimable(now()) + (state.summary ? 1 : 0) : 0);
      const changed = (): void => { if (hubRef) hubRef.changed(); else ctx.persist(); };

      setPassXpSink((source, key) => { const n = season.award(source, key, now()); if (n > 0) ctx.persist(); return n; });
      // Premium and Premium+ through the Store port: the economy feature's PurchaseService routes them here, read on
      // the pass's own clock (the trusted-now floor is Season.now; `?evday` moves it in debug captures).
      economyLink()?.purchases?.addHandler({
        knowsProduct: (id) => season.knowsProduct(id),
        prepare: (id, _now, screen) => season.prepare(id, now(), screen),
        grant: (t) => season.grant(t, now()),
        revoke: (id) => season.revoke(id),
      });

      const open = (hub: HubApi, embed?: HTMLElement): HTMLElement => {
        hubRef = hub;
        ctx.persist();
        return openPass({
          catalog: ctx.catalog, season, parent: hub.parent, now, changed, embed,
          paid: () => economyLink()?.purchases?.dealsContext().paidAllowed ?? false,
          get storeKind() { return economyLink()?.storeKind ?? null; },
          prices: () => economyLink()?.purchases?.storeProducts().then((m) => new Map([...m].map(([id, p]) => [id, p.displayPrice] as const))) ?? Promise.resolve(new Map()),
          buy: (product): Promise<ProductBuyOutcome> => economyLink()?.purchases?.buyProduct(product, "hub") ?? Promise.resolve({ status: "refused", reason: "not on sale" }),
        });
      };

      // owner 2026-10-21: no Pass button on the hub (the Pass is a Store page; its dot joins the Store button's)
      registry.hubWidget({ id: "pass", mount: (hub) => { hubRef = hub; return { dispose: () => { if (hubRef === hub) hubRef = null; } }; } });
      // owner 2026-10-21: the Pass is a Store page (the Store's header and tabs stay)
      addDealsTab({ id: "pass", label: "Pass", icon: PASS_ICON, dot, visible: shown, open: () => { if (hubRef) open(hubRef); },
        mount: (host) => { const hub = hubRef ?? dealsLink()?.hub ?? null; if (hub) open(hub, host); } });

      registry.battleEnd((b) => { if (awardRun(season, b.sim, now()) > 0) ctx.persist(); });
      registry.endScreenExtra({
        id: "pass", order: PASS_END_ORDER,
        html: (f) => {
          const xp = awardRun(season, f.sim, now());
          if (xp > 0) ctx.persist();
          if (xp <= 0 || !season.live(now())) return "";
          const p = tierProgress(ctx.balance.seasons, state.xp);
          const end = p.maxed ? "Every tier" : `Tier ${state.tier} · ${p.into}/${p.need}`;
          return rowHtml({ lead: uiUrl(PASS_ICON), title: `+${xp}`, sub: "War Campaign XP", end: `<small>${end}</small>`, compact: true, className: "erow passxp gain" });
        },
      });

      registry.guideTopic({
        id: "pass", group: "castle", label: "War Campaign", art: { icon: "flag" }, fromLevel: ctx.balance.seasons.unlockAfterLevel + 1, steps: ["hub.pass"],
        lines: [
          "The **War Campaign** is a 28-day season of 30 tiers. Battles, Orders and event rewards all earn its XP.",
          "The free track pays every third tier. **Premium** pays every tier, and it lasts one season.",
          "When a season ends, any reward you reached but didn't claim comes to you.",
        ],
      });

      registry.awayItems(({ awaySeconds }) => {
        if (awaySeconds === null || !shown()) return [];
        const s = state.summary, n = season.claimable(now());
        if (s) return [{ icon: "flag", title: `Season ${s.season + 1} of the War Campaign is over`, detail: `${s.rewards.length} unclaimed reward${s.rewards.length === 1 ? "" : "s"} sent to you · the Pass button` }];
        return n > 0 ? [{ icon: "flag", title: `${n} War Campaign reward${n === 1 ? "" : "s"} to claim`, detail: "The Pass button, under Deals" }] : [];
      });

      if (ctx.debug) debugHooks(ctx, season, now, open, () => hubRef, (days) => { shift += days * SECONDS_PER_DAY; changed(); });
    },
  };
}

/** Pass XP for a finished run (a win, or a loss past the stage floor; an abandoned run earns none), once per run. */
function awardRun(season: Season, sim: LevelSim, now: number): number {
  const won = sim.phase === LevelPhase.Won, lost = sim.phase === LevelPhase.Lost;
  if (!won && !lost) return 0;
  return season.awardBattle({ won, lost, stagesCleared: sim.stagesCleared, key: runKey(sim, won) }, now);
}

/** Captures and smoke tests (`?debug`): open the sheet, add XP, move the pass's clock by days. */
function debugHooks(ctx: FeatureContext, season: Season, now: () => number, open: (hub: HubApi) => HTMLElement, hub: () => HubApi | null, shiftDays: (days: number) => void): void {
  if (typeof window === "undefined") return;
  (window as unknown as { __tdPass: unknown }).__tdPass = {
    season, state: season.state,
    open: () => { const h = hub(); return h ? open(h) : null; },
    view: () => seasonView(ctx.catalog, season, now()),
    xp: (n: number) => { const r = season.award("dailyRoad", `debug${Date.now()}`, now(), n); ctx.persist(); return r; },
    shiftDays,
  };
}

export const seasonFeature: Feature = createSeasonFeature();
